import { PASSWORD_MINIMUM_LENGTH, passwordError } from './passwordRules';

/**
 * Client-side password generator, mirroring the server's `SecureTokenGenerator`.
 *
 * It exists so the create-user form can show the administrator the actual password before
 * the account is created, rather than only revealing it afterwards. The server still
 * validates whatever it is sent, so this is a convenience, never the authority.
 *
 * Ambiguous characters are excluded on both sides — a credential that gets written down or
 * read aloud must not turn on telling `I` from `l` from `1`.
 */

const UPPER = 'ABCDEFGHJKLMNPQRSTUVWXYZ'; // no I/O — misread on paper
const LOWER = 'abcdefghijkmnopqrstuvwxyz'; // no l
const DIGITS = '23456789'; // no 0/1
const SYMBOLS = '!@#$%^&*()-_=+[]{}';
const ALL = UPPER + LOWER + DIGITS + SYMBOLS;

/**
 * Uniform over `alphabet`, using rejection sampling.
 *
 * `% alphabet.length` on a raw byte would bias toward the first characters whenever the
 * length does not divide 256 — true for every alphabet here. Discarding the short tail
 * keeps the distribution flat.
 */
function pickFrom(alphabet: string): string {
  const limit = Math.floor(256 / alphabet.length) * alphabet.length;
  const byte = new Uint8Array(1);

  for (;;) {
    crypto.getRandomValues(byte);

    if (byte[0] < limit) {
      return alphabet[byte[0] % alphabet.length];
    }
  }
}

/** Fisher-Yates over a CSPRNG, so the guaranteed characters land in unpredictable slots. */
function shuffle(chars: string[]): void {
  for (let i = chars.length - 1; i > 0; i--) {
    const j = randomBelow(i + 1);
    [chars[i], chars[j]] = [chars[j], chars[i]];
  }
}

function randomBelow(bound: number): number {
  const limit = Math.floor(256 / bound) * bound;
  const byte = new Uint8Array(1);

  for (;;) {
    crypto.getRandomValues(byte);

    if (byte[0] < limit) {
      return byte[0] % bound;
    }
  }
}

/**
 * A password satisfying every rule in `passwordRules`: one character from each required
 * class first, the remainder from the full alphabet, then shuffled.
 */
export function generatePassword(length = 16): string {
  const target = Math.max(length, PASSWORD_MINIMUM_LENGTH);

  const chars = [pickFrom(UPPER), pickFrom(LOWER), pickFrom(DIGITS), pickFrom(SYMBOLS)];

  while (chars.length < target) {
    chars.push(pickFrom(ALL));
  }

  shuffle(chars);

  const password = chars.join('');

  // Defensive: shuffling cannot drop a character class, but returning something our own
  // validator would reject is worse than failing loudly here.
  if (passwordError(password) !== undefined) {
    throw new Error('Generated password failed the complexity check.');
  }

  return password;
}
